
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  clearPaymentsCache,
  fetchCrn,
  fetchProfile,
  fetchUserDetails,
  fetchPayments,
} from "../../api";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crn, setCrn] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // profile endpoint data
  const [userDetails, setUserDetails] = useState(null); // users endpoint data
  const [payments, setPayments] = useState(null); // payments endpoint data

  // Restore logged-in user from localStorage
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("loggedInUser");
      if (storedUser) {
        setLoggedInUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error("Invalid user data in localStorage");
      localStorage.removeItem("loggedInUser");
    } finally {
      setLoading(false);
    }
  }, []);

  // Persist logged-in user
  useEffect(() => {
    if (loggedInUser) {
      localStorage.setItem("loggedInUser", JSON.stringify(loggedInUser));
    } else {
      localStorage.removeItem("loggedInUser");
    }
  }, [loggedInUser]);

  // Fetch CRN once per session
  useEffect(() => {
    const loadCrn = async () => {
      try {
        const crnValue = await fetchCrn();
        setCrn(crnValue);
      } catch (error) {
        console.error("Failed to fetch CRN", error);
      }
    };
    loadCrn();
  }, []);

  // When user and CRN are available, lazily fetch shared user data
  useEffect(() => {
    const loadUserData = async () => {
      if (!loggedInUser || !crn) return;
      try {
        const [profileRes, userDetailsRes, paymentsRes] = await Promise.all([
          fetchProfile(loggedInUser, crn),
          fetchUserDetails(loggedInUser, crn),
          fetchPayments(loggedInUser, crn),
        ]);
        setUserProfile(profileRes.raw);
        setUserDetails(userDetailsRes.raw);
        setPayments(paymentsRes.raw);
      } catch (error) {
        console.error("Failed to fetch shared user data", error);
      }
    };
    loadUserData();
  }, [loggedInUser, crn]);

  const refreshPayments = async () => {
    if (!loggedInUser || !crn) return;
    try {
      // Payment success writes new rows in the backend; we must bypass in-memory cache.
      clearPaymentsCache();
      const paymentsRes = await fetchPayments(loggedInUser, crn);
      setPayments(paymentsRes.raw);
    } catch (error) {
      console.error("Failed to refresh payments", error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        loggedInUser,
        setLoggedInUser,
        loading,
        crn,
        userProfile,
        userDetails,
        payments,
        refreshPayments,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
